import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ErrorBoundary, PublicLayout } from '@/shared/components';
import { ToastContainer } from '@/shared/components';

const LandingPage = lazy(() => import('./features/landing/LandingPage'));
const TermsPage = lazy(() => import('./features/landing/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./features/landing/LegalPages').then(m => ({ default: m.PrivacyPage })));
const ContactPage = lazy(() => import('./features/landing/components/ContactPage'));
const AboutPage = lazy(() => import('./features/landing/components/AboutPage'));
const UserLoginPage = lazy(() => import('./routes/user/LoginPage'));
const UserDashboardPage = lazy(() => import('./routes/user/DashboardPage'));
const UserSignupPage = lazy(() => import('./routes/user/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./features/password-reset/components/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const UserProfilePage = lazy(() => import('./routes/user/ProfilePage'));
const UserSubscriptionPage = lazy(() => import('./routes/user/SubscriptionPage'));
const UserUsagePage = lazy(() => import('./routes/user/UsagePage'));
const UserHelpPage = lazy(() => import('./routes/user/HelpPage'));
const AcceptInvitePage = lazy(() => import('./routes/user/AcceptInvitePage'));
const DeleteAccountPage = lazy(() => import('./routes/public/DeleteAccountPage'));
const VerifyIdentityPage = lazy(() => import('@/features/billing/pages/VerifyIdentityPage'));
const SubscribePage = lazy(() => import('@/features/billing/pages/SubscribePage'));

const AdminLoginPage = lazy(() => import('./routes/admin/LoginPage'));
const AdminAuthCallback = lazy(() => import('./routes/admin/AuthCallbackPage'));
const AdminDashboardPage = lazy(() => import('./routes/admin/AdminDashboardPage'));
const AdminMetricsPage = lazy(() => import('./routes/admin/MetricsPage'));
const AdminUsersPage = lazy(() => import('./routes/admin/UsersPage'));
const AdminUsagePage = lazy(() => import('./routes/admin/UsagePage'));
const AdminPricingPage = lazy(() => import('./routes/admin/PricingPage'));
const AdminStaticDataPage = lazy(() => import('./routes/admin/StaticDataPage'));
const AdminExamModelsPage = lazy(() => import('./routes/admin/ExamModelsPage'));
const AdminBillingDashboardPage = lazy(() => import('./routes/admin/BillingDashboardPage'));
const AdminBillingCustomersPage = lazy(() => import('./routes/admin/BillingCustomersPage'));
const AdminPublicAssetsPage = lazy(() => import('./routes/admin/PublicAssetsPage'));

const UserPortalRouter = lazy(() => import('./routes/user/UserPortalRouter'));
const AdminPortalRouter = lazy(() => import('./routes/admin/AdminPortalRouter'));

function RouteLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 dark:border-slate-100"></div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundaryWrapper>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/user/login" element={<UserLoginPage />} />
              <Route path="/user/signup" element={<UserSignupPage />} />
              <Route path="/user/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
            </Route>

            <Route path="/user" element={<UserPortalRouter />}>
              <Route index element={<Navigate to="/user/dashboard" replace />} />
              <Route path="dashboard" element={<UserDashboardPage />} />
              <Route path="profile" element={<UserProfilePage />} />
              <Route path="subscription" element={<UserSubscriptionPage />} />
              <Route path="usage" element={<UserUsagePage />} />
              <Route path="help" element={<UserHelpPage />} />
              <Route path="verify-identity" element={<VerifyIdentityPage />} />
              <Route path="subscribe" element={<SubscribePage />} />
            </Route>

            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/auth/callback" element={<AdminAuthCallback />} />
            <Route path="/admin" element={<AdminPortalRouter />}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="metrics" element={<AdminMetricsPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="usage" element={<AdminUsagePage />} />
              <Route path="pricing" element={<AdminPricingPage />} />
              <Route path="static-data" element={<AdminStaticDataPage />} />
              <Route path="exam-models" element={<AdminExamModelsPage />} />
              <Route path="billing" element={<AdminBillingDashboardPage />} />
              <Route path="customers" element={<AdminBillingCustomersPage />} />
              <Route path="public-assets" element={<AdminPublicAssetsPage />} />
            </Route>

            <Route path="*" element={<CatchAllRedirect />} />
          </Routes>
        </Suspense>
        <ToastContainer />
      </ErrorBoundaryWrapper>
    </QueryClientProvider>
  );
}

function ErrorBoundaryWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const handleNavigateHome = () => {
    navigate('/', { replace: true });
  };

  return <ErrorBoundary onNavigateHome={handleNavigateHome}>{children}</ErrorBoundary>;
}

function CatchAllRedirect() {
  const location = useLocation();

  let redirectTo = '/';
  const pathname = location.pathname || '';

  if (pathname.startsWith('/admin')) {
    redirectTo = pathname === '/admin/login' ? '/admin/login' : '/admin';
  } else if (pathname.startsWith('/user')) {
    redirectTo = '/user/dashboard';
  }

  return <Navigate to={redirectTo} replace />;
}

export default App;
